import React from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MenuBookIcon from "@mui/icons-material/MenuBook";

// ── S3 base URL — update this to your actual S3/CloudFront URL ──
const S3_BASE = "https://app.nephrotrax.com/guides";

const GUIDES = [
  {
    title: "Understanding AL Amyloidosis",
    subtitle: "Primary Amyloidosis",
    description:
      "A comprehensive guide covering AL amyloidosis diagnosis, Mayo staging system, treatment options including CyBorD and daratumumab, clinical trials, and key questions to ask your doctor.",
    file: "AL_Amyloidosis_Guide.pdf",
    color: "#1A5EA8",
    emoji: "🔬",
    pages: "4 pages",
    topics: ["Diagnosis", "Mayo Staging", "Treatment", "Clinical Trials"],
    updated: "May 2026",
  },
  {
    title: "Understanding ATTR Amyloidosis",
    subtitle: "Hereditary & Wild-type ATTR",
    description:
      "Covers both ATTRv (hereditary) and ATTRwt (wild-type) amyloidosis — diagnosis with PYP scan, all FDA-approved treatments including tafamidis, acoramidis, patisiran, and vutrisiran, plus genetic testing guidance.",
    file: "ATTR_Amyloidosis_Guide.pdf",
    color: "#1a5fa8",
    emoji: "🫀",
    pages: "4 pages",
    topics: ["ATTRv vs ATTRwt", "PYP Scan", "FDA Treatments", "Genetic Testing"],
    updated: "May 2026",
  },
  {
    title: "Understanding AA Amyloidosis",
    subtitle: "Secondary Amyloidosis",
    description:
      "Explains AA amyloidosis as a complication of chronic inflammation — underlying causes, kidney involvement, SAA monitoring, biologic treatments, and how controlling inflammation can stabilize or reverse amyloid deposits.",
    file: "AA_Amyloidosis_Guide.pdf",
    color: "#11998e",
    emoji: "🧬",
    pages: "4 pages",
    topics: ["Underlying Causes", "Kidney Effects", "SAA Monitoring", "Biologics"],
    updated: "May 2026",
  },
];

const DownloadableGuides = () => {
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 5, textAlign: "center" }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: "bold",
            background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Patient Guides
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Free downloadable guides for patients, caregivers, and families
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Print them out, share with family, or bring to your next appointment
        </Typography>
      </Box>

      {/* Guide Cards */}
      <Grid container spacing={4} justifyContent="center">
        {GUIDES.map((guide, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s ease",
                border: "2px solid",
                borderColor: "grey.200",
                boxShadow: 2,
                borderRadius: 3,
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: 8,
                  borderColor: guide.color,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Icon + title */}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 60, height: 60, borderRadius: 2, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: `linear-gradient(135deg, ${guide.color}22 0%, ${guide.color}44 100%)`,
                      fontSize: "30px",
                    }}
                  >
                    {guide.emoji}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                      {guide.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: guide.color, fontWeight: 600 }}>
                      {guide.subtitle}
                    </Typography>
                  </Box>
                </Box>

                {/* Description */}
                <Typography variant="body2" color="text.secondary"
                  sx={{ mb: 2.5, lineHeight: 1.65 }}>
                  {guide.description}
                </Typography>

                {/* Topics */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
                  {guide.topics.map((topic) => (
                    <Chip
                      key={topic}
                      label={topic}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        bgcolor: `${guide.color}15`,
                        color: guide.color,
                        fontWeight: 500,
                        border: `1px solid ${guide.color}30`,
                      }}
                    />
                  ))}
                </Box>

                {/* Meta */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PictureAsPdfIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary">
                      {guide.pages}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <MenuBookIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary">
                      Updated {guide.updated}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>

              <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  href={`${S3_BASE}/${guide.file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  sx={{
                    background: `linear-gradient(135deg, ${guide.color} 0%, ${guide.color}cc 100%)`,
                    fontWeight: 600,
                    borderRadius: 2,
                    py: 1.25,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${guide.color}dd 0%, ${guide.color}aa 100%)`,
                    },
                  }}
                >
                  Download PDF
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Coming soon guides */}
      <Box sx={{ mt: 6, p: 4, borderRadius: 3, bgcolor: "rgba(102,126,234,0.05)",
        border: "1px dashed", borderColor: "rgba(102,126,234,0.3)", textAlign: "center" }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom
          sx={{ background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            backgroundClip: "text", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          More Guides Coming Soon
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          We're working on additional guides including:
        </Typography>
        {["Navigating Clinical Trials — A Patient Handbook",
          "Understanding Your Lab Results",
          "Talking to Your Doctor About Amyloidosis",
          "Financial Assistance Guide for Amyloidosis Patients",
          "Caregiver's Guide to Amyloidosis"].map((title) => (
          <Chip key={title} label={title} size="small" variant="outlined"
            sx={{ m: 0.5, borderColor: "rgba(102,126,234,0.4)", color: "#1A5EA8" }} />
        ))}
      </Box>

      {/* Footer note */}
      <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider", textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          These guides are for informational purposes only and do not constitute medical advice.
          Always consult your healthcare provider. Questions?{" "}
          <Box component="span" sx={{ color: "#1A5EA8", fontWeight: 600 }}>
            support@nephrotrax.com
          </Box>
        </Typography>
      </Box>
    </Container>
  );
};

export default DownloadableGuides;